import { DOCUMENT, formatDate } from '@angular/common';
import { Component, Inject, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { GetContextMenuItemsParams, GridOptions, SortDirection, SuppressKeyboardEventParams } from 'ag-grid-community';
import { ToastrManager } from '@niceltd/cxone-components/toastr';
import { DashboardService } from 'src/app/dashboard/dashboard.service';
import { CreateNewReportModalComponent } from 'src/app/dashboard/modals/create-new-report-modal/create-new-report-modal.component';
import { DashboardNavigationService } from 'src/app/navigation/dashboard-navigation.service';
import { NavigationService } from "src/app/navigation/navigation.service";
import { SysTemplateActionCellRendererComponent } from 'src/app/reports/manage-reports/systemplate-action-cell-renderer/systemplate-action-cell-renderer.component';
import { CvAngularLoadingOverlayComponent } from 'src/app/reusables/ag-grid-components/cv-angular-loading-overlay/cv-angular-loading-overlay.component';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { Status, SystemTemplateWto } from 'src/shared/models/dashboard.model';
import { DescriptionCellRendererComponent } from '../../dashboard/workspace/manage-available-dashboards/ag-grid-components/description-renderer/description-renderer.component';
import { CvConfigurationService } from '../../reusables/services/cv-configuration.service';
import { SystemplateCellRendererComponent } from './systemplate-cell-renderer/systemplate-cell-renderer.component';
import { SystemplateStatusCellRendererComponent } from './systemplate-status-cell-renderer/systemplate-status-cell-renderer/systemplate-status-cell-renderer.component';
import { SystemTemplateModel } from 'src/shared/models/system-template.models';
import { SystemTemplateService } from 'src/app/dashboard/system-templates/system-template.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'manage-reports',
    templateUrl: './manage-reports.component.html',
    styleUrls: ['./manage-reports.component.scss']
})
export class ManageReportsComponent implements OnInit {
    @ViewChild('manageReportsGrid') agGrid: AgGridAngular;

    public gridOptions: GridOptions = null;
    public noData = false;
    public data: SystemTemplateWto[] = [];

    private gridState = null;
    private isDataLoading = false;
    private isFirstLoad = true;
    private isCXOne = true;

    modalRef = null;
    filterValue = '';
    searchFilter = '';
    localeText = {}; // object for ag grid localization
    showLoader = false;

    constructor(@Inject(LOCALE_ID) private locale: string,
        private dashboardService: DashboardService,
        private dashboardNavigationService: DashboardNavigationService,
        private agGridHelperService: AgGridHelperService,
        private cvLocalizationService: CVLocalizationService,
        private config: CvConfigurationService,
        private navigationService: NavigationService,
        private ngbModal: NgbModal,
        private systemTemplateService: SystemTemplateService,
        @Inject(DOCUMENT) private document: Document,
        public toastrManager: ToastrManager) {
        if (this.isCXOne) {
            this.document.body.classList.add('cx-one-ui');
            this.config.isCxOne = true;
        }
    }

    async ngOnInit() {
        this.localeText = await this.agGridHelperService.getLocalizations();
        await this.cvLocalizationService.GetSection("Manage_Reports");
        await this.setupGrid();
        await this.loadData();
    }

    applyFilter() {
        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.updateNoRowsMessage();
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    };

    clearFilter() {
        this.filterValue = "";
        if (this.agGrid?.api) { // notify filter change
            this.agGrid?.api.setGridOption('quickFilterText', this.filterValue);

            this.applyFilter();
        }
    };

    getLocaleText(key: string, defaultValue?: string) {
        return this.cvLocalizationService.getCachedText("Manage_Reports", key, defaultValue);
    }

    private async loadData() {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        try {
            this.data = await this.dashboardService.loadSystemTemplates(this.config.isCxOne, true, true);
            this.noData = this.data.length === 0;
            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
            }
        }
        finally {
            this.isDataLoading = false;
        }
    }

    public createColDefs() {
        return [
            {
                field: "SystemTemplateName",
                colId: "SystemTemplateName",
                headerName: this.getLocaleText("Name_Heading", "Name"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: "systemplateCellRenderer",
                cellRendererParams: {
                    dashboardClicked: (key: string) => this.openSystemTemplate(key),
                },
                menuTabs: this.isCXOne ? null : [],
                sort: this.isCXOne ? 'asc' as SortDirection : null,
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 100 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: 'PermissionName',
                colId: 'PermissionName',
                headerName: this.getLocaleText("Permission_Name", "Permission Name"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: 'descriptionCellRenderer',
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "Status",
                colId: "Status",
                headerName: this.getLocaleText("State_Heading", "State"),
                cellRenderer: 'systemplateStatusCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                filterValueGetter: params => {
                    if (params.data) {
                        switch (params.data.Status) {
                            case Status.Draft:
                                return this.getLocaleText("Draft", "Draft");
                            case Status.Published_Draft:
                                return this.getLocaleText("Published_Draft", "Published - Draft");
                            case Status.Published:
                                return this.getLocaleText("Published", "Published");
                            case Status.Unpublished:
                                return this.getLocaleText("Unpublished", "Unpublished");
                        }
                    }
                },
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 100 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "SystemTemplateDesc",
                colId: "SystemTemplateDesc",
                headerName: this.getLocaleText("Description_Heading", "Description"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: 'descriptionCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "SelectedDomains",
                colId: "SelectedDomains",
                headerName: this.getLocaleText("Selected_Domains", "Domains"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: 'descriptionCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "UserType",
                colId: "UserType",
                headerName: this.getLocaleText("User_Type", "Persona"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: 'descriptionCellRenderer',
                menuTabs: this.isCXOne ? null : [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "LastModifiedDate",
                colId: "LastModifiedDate",
                headerName: this.getLocaleText("Last_Modified_Heading", "Last Modified"),
                valueFormatter: params => {
                    if (!params.value)
                        return "-";
                    return this.formatDate(params.value);
                },
                filterValueGetter: params => {
                    if (!params.data.LastModifiedDate)
                        return "-";
                    return this.formatDate(params.data.LastModifiedDate);
                },
                menuTabs: this.isCXOne ? null : [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "LastModifiedBy",
                colId: "ModifiedBy",
                headerName: this.getLocaleText("Modified_by_Heading", "Modified by"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                menuTabs: this.isCXOne ? null : [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                minWidth: this.isCXOne ? 120 : null,
                maxWidth: this.isCXOne ? 300 : null,
            },
            {
                field: "Actions",
                colId: "Actions",
                headerName: this.getLocaleText("Actions_Heading", "Actions"),
                valueFormatter: params => this.sanitizeName(params.value) ?? '',
                cellRenderer: "systemplateActionsCellRenderer",
                minWidth: this.isCXOne ? 100 : null,
                maxWidth: this.isCXOne ? 100 : null,
                sortable: false,
                resizable: false,
                menuTabs: [],
                suppressKeyboardEvent: (params: SuppressKeyboardEventParams) => {
                    if (params.event.key === "Enter" || params.event.code === "Enter") {
                        return true;
                    }
                    else
                        return false;
                },
                headerClass: 'ActionsCenterHeader',
                cellStyle: () => ({
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }),
                lockVisible: true,
            },
        ];
    }

    private setupGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                cvAngularLoadingComponent: CvAngularLoadingOverlayComponent,
                systemplateActionsCellRenderer: SysTemplateActionCellRendererComponent,
                systemplateCellRenderer: SystemplateCellRendererComponent,
                systemplateStatusCellRenderer: SystemplateStatusCellRendererComponent,
                descriptionCellRenderer: DescriptionCellRendererComponent,
            },
            defaultColDef: {
                enableCellChangeFlash: true,
                sortable: true,
                resizable: true,
                filter: true,
            },
            columnDefs: this.createColDefs(),
            rowData: [],
            onGridReady: params => {
                params.api.showLoadingOverlay();
            },
            onFirstDataRendered: params => {
                params.api.sizeColumnsToFit();
            },
            onColumnVisible: event => {
                let visibleColumns = 0;
                this.agGrid?.api.getColumns().forEach((c) => {
                    if (c.isVisible()) {
                        visibleColumns++;
                    }
                });
                if (visibleColumns === 1) {
                    this.agGrid?.api.setColumnVisible(this.agGrid?.api.getColumns()[0].getColId(), true);
                }
                if (this.agGrid?.api && this.isCXOne)
                    event.api.sizeColumnsToFit();
            },
            onGridSizeChanged: params => {
                if (this.agGrid?.api && this.isCXOne)
                    this.agGrid?.api.sizeColumnsToFit();
            },
            isExternalFilterPresent: () => this.filterValue.length > 0,
            loadingOverlayComponent: 'cvAngularLoadingComponent',
            getRowId: ({ data: rowData }) => rowData.SystemTemplateID,
            getContextMenuItems: params => this.initializeContextMenu(params),
            rowSelection: "single",
            suppressCellFocus: !this.isCXOne, // cell selection is required for accessible keyboard controls
            suppressMovableColumns: !this.isCXOne,
            suppressDragLeaveHidesColumns: true,
            suppressContextMenu: true,
            localeText: this.localeText,
            headerHeight: this.isCXOne ? 45 : null,
            rowHeight: this.isCXOne ? 36 : null,
            icons: this.isCXOne ? {
                sortAscending: '<div class="sortIcons"><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                sortDescending: '<div class="sortIcons"><svg class="icon-10 fill-sort"><use href="../App_Themes/CXone/icons/arrow-up.svg#arrow-up"></use></svg><svg class="icon-10 fill-cxoneBlue"><use href="../App_Themes/CXone/icons/arrow-down.svg#arrow-down"></use></svg></div>',
                filter: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/filter.svg#filter"></use></svg>',
                menu: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/widget-menu.svg#widget-menu"></use></svg>',
                columns: '<svg class="icon-20 fill-iconMenu"><use href="../App_Themes/CXone/icons/chart.svg#chart"></use></svg>',
            } : null,
            ensureDomOrder: true,
            rowClass: 'cxOneRow',
        });

    }

    private getSelectedRow() {
        return this.agGrid?.api.getSelectedNodes()[0];
    }

    private initializeContextMenu(params: GetContextMenuItemsParams) {
        let contextMenu = [];
        contextMenu = [
            {
                name: this.getLocaleText("Edit", "Edit"),
                action: () => this.editDetailsClicked(this.getSelectedRow()),
                icon: this.isCXOne ?
                    `<svg class="icon-16 fill-iconDefault-noHover">
                        <use href="../App_Themes/CXone/icons/edit.svg#edit"></use>
                    </svg>` :
                    `<svg class="icon-16 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/edit.svg#edit"></use>
                    </svg>`,
                cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
            },
            {
                name: this.getLocaleText("Duplicate", "Duplicate"),
                action: () => this.duplicateClicked(this.getSelectedRow()),
                icon: this.isCXOne ?
                    `<svg class="icon-16 fill-iconDefault-noHover">
                        <use href="../App_Themes/CXone/icons/duplicate.svg#duplicate"></use>
                    </svg><div style=""></div>` :
                    `<svg class="icon-16 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/copy.svg#copy"></use>
                    </svg>`,
                cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
            },
        ];

        if (params.node.data.Status === Status.Draft || params.node.data.Status === Status.Published_Draft || params.node.data.Status === Status.Unpublished) {
            contextMenu.splice(2, 0, {
                name: this.getLocaleText("Publish", "Publish"),
                action: () => this.publishClicked(this.getSelectedRow()),
                icon: this.isCXOne ?
                    `<svg class="icon-16 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/export.svg#export"></use>
                    </svg>`: null,
                cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
            });
        } else if (params.node.data.Status == Status.Published) {
            contextMenu.splice(2, 0, {
                name: this.getLocaleText("Unpublish", "Unpublish"),
                action: () => this.unpublishClicked(this.getSelectedRow()),
                icon: this.isCXOne ?
                    `<svg class="icon-16 fill-valyrianSteel">
                        <use href="../App_Themes/clearview-default/icons/utility/export.svg#export"></use>
                    </svg>` : null,
                cssClasses: this.isCXOne ? ['isCXoneMenu'] : null
            });
        }

        if (contextMenu.length === 0)
            return [
                {
                    name: "No Actions Available"
                }
            ];
        else
            return contextMenu;
    }

    openCreateNewReportModal() {
        this.navigationService.showOverlay = true;
        const type = "Create";
        this.modalRef = this.ngbModal.open(CreateNewReportModalComponent,
            { size: "med", centered: true, scrollable: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null });
        this.modalRef.componentInstance.type = type;
        this.modalRef.result.then(
            (savedTemplateKey: string) => {
                this.showLoader = true;
                if (savedTemplateKey) {
                    this.toastrManager.showToaster("success", this.getLocaleText("Report_Created", "Report created successfully"));
                    this.openSystemTemplate(savedTemplateKey);
                }
            }
        );
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    private editDetailsClicked(currentRow) {
        this.navigationService.showOverlay = true;
        const type = "Edit";
        this.modalRef = this.ngbModal.open(CreateNewReportModalComponent,
            { size: "med", centered: true, scrollable: true, modalDialogClass: this.config.isCxOne ? 'cx-one-ui' : null });
        this.modalRef.componentInstance.type = type;
        this.modalRef.componentInstance.editData = currentRow.data;
        this.modalRef.result.then(
            (data: SystemTemplateModel) => {
                if (data) {
                    currentRow.data.SystemTemplateName = data.name;
                    currentRow.data.SystemTemplateDesc = data.description;
                    currentRow.data.SelectedDomains = data.selectedDomains;
                    currentRow.data.UserType = data.userType;
                    currentRow.data.Status = data.status;
                    currentRow.data.LastModifiedDate = data.lastModifiedDate;
                    currentRow.data.LastModifiedBy = data.lastModifiedBy;
                    currentRow.data.PermissionName = data.permissionName;
                    currentRow.updateData(currentRow.data);
                    this.toastrManager.showToaster("success", this.getLocaleText("Report_Updated", "Report updated successfully"));
                }
            }
        );
        this.modalRef.result.finally(() => this.navigationService.showOverlay = false);
    }

    private duplicateClicked(currentRow) {
        alert("Duplicate Clicked: " + currentRow.SystemTemplateName);
    }

    private async publishClicked(currentRow) {
        currentRow.data.Status = Status.Published;

        // converts Wto object to model object
        const systemTemplateModel: SystemTemplateModel = {
            key: currentRow.data.SystemTemplateKey,
            name: currentRow.data.SystemTemplateName,
            description: currentRow.data.SystemTemplateDesc,
            personas: currentRow.data.Personas,
            userType: currentRow.data.UserType,
            group: currentRow.data.SystemTemplateGroup,
            integrations: currentRow.data.Integrations,
            selectedDomains: currentRow.data.SelectedDomains,
            status: currentRow.data.Status,
            lastModifiedDate: currentRow.data.LastModifiedDate,
            lastModifiedBy: currentRow.data.LastModifiedBy,
            permissionName: currentRow.data.PermissionName,
        };

        const response = await this.systemTemplateService.updateCxOneSystemTemplate(systemTemplateModel);;
        if (currentRow.data.LastModifiedBy != response.data[0]) {
            currentRow.data.LastModifiedBy = response.data[0];
        }
        if (currentRow.data.LastModifiedDate.substring(0, 19) != response.data[1].substring(0, 19)) {
            currentRow.data.LastModifiedDate = response.data[1];
        }

        currentRow.updateData(currentRow.data);

        this.toastrManager.showToaster("success", this.getLocaleText("Report_Published", "Report published successfully"));
    }

    private async unpublishClicked(currentRow) {
        currentRow.data.Status = Status.Unpublished;

        // converts Wto object to model object
        const systemTemplateModel: SystemTemplateModel = {
            key: currentRow.data.SystemTemplateKey,
            name: currentRow.data.SystemTemplateName,
            description: currentRow.data.SystemTemplateDesc,
            personas: currentRow.data.Personas,
            userType: currentRow.data.UserType,
            group: currentRow.data.SystemTemplateGroup,
            integrations: currentRow.data.Integrations,
            selectedDomains: currentRow.data.SelectedDomains,
            status: currentRow.data.Status,
            lastModifiedDate: currentRow.data.LastModifiedDate,
            lastModifiedBy: currentRow.data.LastModifiedBy,
            permissionName: currentRow.data.PermissionName,
        };

        const response = await this.systemTemplateService.updateCxOneSystemTemplate(systemTemplateModel);;
        if (currentRow.data.LastModifiedBy != response.data[0]) {
            currentRow.data.LastModifiedBy = response.data[0];
        }
        if (currentRow.data.LastModifiedDate.substring(0, 19) != response.data[1].substring(0, 19)) {
            currentRow.data.LastModifiedDate = response.data[1];
        }

        currentRow.updateData(currentRow.data);

        this.toastrManager.showToaster("success", this.getLocaleText("Report_Unpublished", "Report unpublished successfully"));
    }

    private async openSystemTemplate(templateKey: string) {
        const dashboard = await this.dashboardService.copyTemporaryDashboardFromSystemTemplate(templateKey, true);
        await this.dashboardService.clearWorkspace(this.dashboardService.workspaceKey, true);
        await this.dashboardService.clearDashboardsFromWorkspace();
        await this.dashboardService.addDashboardToWorkspace(this.dashboardService.workspaceKey, dashboard, true, true);
        await this.dashboardNavigationService.navigateToCXOneDashboardView();
    }

    private formatDate(dateString: string) {
        let date = dateString;
        date = date.substring(0, 19); // length of ISO datetime string without time zone info
        if (this.isCXOne) {
            const dateObj = new Date(date);
            const dateDifference = dateObj.getElapsed(Date.today()) / (60 * 60 * 24 * 1000);
            if (dateDifference >= 7)
                return formatDate(date, 'mediumDate', this.locale);
            else if (dateDifference > 1) {
                const DOW: string = formatDate(date, "EEEE", this.locale);
                return `${DOW} ${formatDate(date, "hh:mm a", this.locale)}`;
            }
            else if (dateDifference > 0)
                return `Yesterday ${formatDate(date, "hh:mm a", this.locale)}`;
            else
                return `Today ${formatDate(date, "hh:mm a", this.locale)}`;
        }
        else
            return formatDate(date, CVAngularDateFormat, this.locale);
    }

    private sanitizeName(name: string) {
        return name?.replace(/</gi, "&#60;").replace(/=/gi, "&#61;").replace(/>/gi, "&#62;");
    }

    private updateNoRowsMessage() {
        if (this.isCXOne) {
            if (this.dashboardService.loadExistingTemplatesFailed) {
                this.gridOptions.overlayNoRowsTemplate =
                    `<div>
                        <svg class="cxoneErrorState mb-2">
                            <use href="../App_Themes/CXone/icons/data-error.svg#data-error"></use>
                        </svg>
                        <p class="cxoneEmptyText">${this.cvLocalizationService.getCachedText("Manage_Reports", "Load_Error", "Cannot load data")}</p>
                    </div>`;
            }
            // -- no search results
            if (this.filterValue.length > 0) {
                this.gridOptions.overlayNoRowsTemplate =
                    `<div>
                        <svg class="cxoneFilterState mb-2" style="padding-top: 32px;">
                            <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                        </svg>
                        <p class="cxoneEmptyText">${this.cvLocalizationService.getCachedText("Manage_Reports", "No_Results", "No results found")}</p>
                    </div>`;
            }
            // -- no reports
            if (this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length === 0) {
                this.gridOptions.overlayNoRowsTemplate =
                    `<div>
                        <svg class="cxoneEmptyState mb-2">
                            <use href="../App_Themes/CXone/icons/add-dashboard.svg#add-dashboard"></use>
                        </svg>
                        <p class="cxoneEmptyText">${this.cvLocalizationService.getCachedText("Manage_Reports", "No_Dashboards_Added", "No dashboards added yet")}</p>
                    </div>`;
            }
        } // if not CXOne, the error message should stay the default
    }
}
